import { ITaxonomyTerms } from '@kontent-ai/delivery-sdk';
import { ProdClient } from 'client/client';
import { FeaturedContent } from 'components/modules/FeaturedContent';
import { HeadComponent } from 'components/pages/Head';
import OverviewTemplate from 'components/pages/Overview';
import { fetchInitialUrlFilter } from 'helpers/getLocaleKey';
import { SEGMENT_SECTOR_MAPPING } from 'lib/constants';
import { flatMapDeep } from 'lodash';
import cacheData from 'memory-cache';
import { ContentPage, FeaturedSpots, OverviewPage } from 'models';
import { useRouter } from 'next/router';
// import contentPages from 'pages/api/contentPages';
import { useEffect, useState } from 'react';
import slugify from 'slugify';
import { withTranslations } from 'store/translations';
import { alphabeticalSort } from 'utils/alphabeticalSort';
import { onlyDefined } from 'utils/definedInArray';
import { fetchOverviewVariants } from 'utils/getOverviewPage';
import { OptionsProps } from 'utils/overviewFunctionality';
import { getSearchParams } from 'utils/urlHelper';

interface ProjectOverviewProps {
  ContentPages: ContentPage[];
  FeaturedSpot: FeaturedSpots;
  Sector: ITaxonomyTerms[];
  Segment: ITaxonomyTerms[];
  Location: ITaxonomyTerms[];
  ServiceLine: ITaxonomyTerms[];
  urlSector: string | null;
  OverviewPage: OverviewPage;
}

export default function ProjectOverview({
  ContentPages,
  FeaturedSpot,
  Sector,
  Segment,
  Location,
  ServiceLine,
  urlSector,
  OverviewPage,
}: ProjectOverviewProps) {
  const {
    common,
    multiple,
    category,
    location: locationTranslate,
    sector: sectorTranslate,
    segment: segmentTranslate,
    service_line: serviceLineTranslate,
  } = withTranslations();
  const translations = withTranslations();
  // ------------------------ FUNCTIONS ------------------------
  // -- Divide pages to show into arrays of 10 pages --

  const dividePagesToShow = () => {
    const pagesToShowTemp: ContentPage[][] = [[]];
    let dividePagesByTen = 0;

    ContentPages.forEach((page) => {
      const sectorValues = page.elements.tagsSector.value;
      const sectors = sectorValues.length > 0 ? sectorValues : [null]; // normalize empty sector array
      sectors.forEach((sector) => {
        const sectorCodename = sector?.codename;
        const locationCodename = page.elements.tagsLocation.value[0]?.codename;
        const serviceLineCodename = page.elements.tagsServiceLine.value[0]?.codename;
        const segmentCodename: any = Array.isArray(page.elements.tagsSegment?.value)
          ? page.elements.tagsSegment.value.map((term) => {
              return term.codename;
            })
          : [];

        const isMatch =
          (filterSector.includes(sectorCodename) || !filterSector[0]) &&
          (filterLocation.includes(locationCodename) || !filterLocation[0]) &&
          (filterServiceLine.includes(serviceLineCodename) || !filterServiceLine[0]) &&
          ((Array.isArray(filterSegment) && segmentCodename.some((item) => filterSegment.includes(item))) ||
            !filterSegment[0]);

        if (isMatch) {
          // Prevent duplicates
          const alreadyAdded = pagesToShowTemp.flat().some((p) => p.system.codename === page.system.codename);
          if (!alreadyAdded) {
            if (pagesToShowTemp[dividePagesByTen].length >= 10) {
              dividePagesByTen++;
              pagesToShowTemp[dividePagesByTen] = [];
            }
            pagesToShowTemp[dividePagesByTen].push(page);
          }
        }
      });
    });

    return onlyDefined(pagesToShowTemp);
  };

  const getChildren = (member) => {
    if (!member.terms || !member.terms.length) {
      return member;
    }
    return [member, flatMapDeep(member.terms, getChildren)];
  };

  // -- Functions that check amount of pages with filters --
  const updateFilterLocation = () => {
    //setPagination(0);
    return Location.map((location) => {
      let amountOfPagesWithTag = 0;
      ContentPages.forEach((page) => {
        const locationCodename = page.elements.tagsLocation.value[0]?.codename;
        const serviceLineCodename = page.elements.tagsServiceLine.value[0]?.codename;
        const sectorCodename: any = Array.isArray(page.elements.tagsSector?.value)
          ? page.elements.tagsSector.value.map((term) => {
              return term.codename;
            })
          : [];
        const segmentCodename: any = Array.isArray(page.elements.tagsSegment?.value)
          ? page.elements.tagsSegment.value.map((term) => {
              return term.codename;
            })
          : [];
        if (
          location.codename === locationCodename &&
          (filterServiceLine.includes(serviceLineCodename) || !filterServiceLine[0]) &&
          ((Array.isArray(filterSector) && sectorCodename.some((item) => filterSector.includes(item))) ||
            !filterSector[0]) &&
          ((Array.isArray(filterSegment) && segmentCodename.some((item) => filterSegment.includes(item))) ||
            !filterSegment[0])
        ) {
          amountOfPagesWithTag++;
        }
      });
      return Object.assign({
        amountOfPagesWithTag: amountOfPagesWithTag,
        ...location,
        name: locationTranslate(location.codename),
      });
    }).sort((a, b) => alphabeticalSort(a.name, b.name));
  };

  const updateFilterSector = () => {
    //setPagination(0);
    // console.log('sector list ',Sector)
    return Sector.map((sector) => {
      let amountOfPagesWithTag = 0;
      ContentPages.forEach((page) => {
        page.elements.tagsSector.value.forEach((tag) => {
          const sectorCodename = tag.codename;
          const locationCodename = page.elements.tagsLocation.value[0]?.codename;
          const serviceLineCodename = page.elements.tagsServiceLine.value[0]?.codename;
          const segmentCodename: any = Array.isArray(page.elements.tagsSegment?.value)
            ? page.elements.tagsSegment.value.map((term) => {
                return term.codename;
              })
            : [];
          if (
            sector.codename === sectorCodename &&
            (filterLocation.includes(locationCodename) || !filterLocation[0]) &&
            (filterServiceLine.includes(serviceLineCodename) || !filterServiceLine[0]) &&
            ((Array.isArray(filterSegment) && segmentCodename.some((item) => filterSegment.includes(item))) ||
              !filterSegment[0])
          ) {
            amountOfPagesWithTag++;
          }
        });
      });
      return Object.assign({
        amountOfPagesWithTag: amountOfPagesWithTag,
        ...sector,
        name: sectorTranslate(sector.codename),
      });
    }).sort((a, b) => alphabeticalSort(a.name, b.name));
  };

  const updateFilterSegment = () => {
    //setPagination(0);
    // console.log('sector list ',Sector)
    return Segment.map((segItem) => {
      let amountOfPagesWithTag = 0;
      ContentPages.forEach((page) => {
        page.elements.tagsSegment?.value.forEach((tag) => {
          const segmentCodename = tag.codename;
          const locationCodename = page.elements.tagsLocation.value[0]?.codename;
          const serviceLineCodename = page.elements.tagsServiceLine.value[0]?.codename;
          const sectorCodename: any = Array.isArray(page.elements.tagsSector?.value)
            ? page.elements.tagsSector.value.map((term) => {
                return term.codename;
              })
            : [];
          if (
            segItem.codename === segmentCodename &&
            (filterLocation.includes(locationCodename) || !filterLocation[0]) &&
            (filterServiceLine.includes(serviceLineCodename) || !filterServiceLine[0]) &&
            ((Array.isArray(filterSector) && sectorCodename.some((item) => filterSector.includes(item))) ||
              !filterSector[0])
          ) {
            amountOfPagesWithTag++;
          }
        });
      });
      return Object.assign({
        amountOfPagesWithTag: amountOfPagesWithTag,
        ...segItem,
        name: segmentTranslate(segItem.codename),
      });
    }).sort((a, b) => alphabeticalSort(a.name, b.name));
  };

  const updateFilterServiceLine = () => {
    //setPagination(0);
    return ServiceLine.map((serviceLine) => {
      let amountOfPagesWithTag = 0;
      ContentPages.forEach((page) => {
        const sectorCodename = page.elements.tagsSector.value[0]?.codename;
        const locationCodename = page.elements.tagsLocation.value[0]?.codename;
        const serviceLineCodename = page.elements.tagsServiceLine.value[0]?.codename;
        const segmentCodename: any = Array.isArray(page.elements.tagsSegment?.value)
          ? page.elements.tagsSegment.value.map((term) => {
              return term.codename;
            })
          : [];
        if (
          serviceLine.codename === serviceLineCodename &&
          (filterLocation.includes(locationCodename) || !filterLocation[0]) &&
          (filterSector.includes(sectorCodename) || !filterSector[0]) &&
          ((Array.isArray(filterSegment) && segmentCodename.some((item) => filterSegment.includes(item))) ||
            !filterSegment[0])
        ) {
          amountOfPagesWithTag++;
        }
      });
      return Object.assign({
        amountOfPagesWithTag: amountOfPagesWithTag,
        ...serviceLine,
        name: serviceLineTranslate(serviceLine.codename),
      });
    }).sort((a, b) => alphabeticalSort(a.name, b.name));
  };
  // -- Functions that check amount of pages with filters End --

  // -- URL based functions --
  const updateFiltersInUrl = () => {
    const removeQueries = router.asPath.includes('?') && router.asPath.split('?').reverse().pop();
    const urlArray =
      removeQueries !== false && removeQueries !== undefined ? removeQueries.split('/') : router.asPath.split('/');
    urlArray.length >= 3 && urlArray.pop();
    // urlArray.push(...filterSector.map((sector) => slugify(sectorTranslate(sector), { lower: true })));
    let urlString = urlArray.join('/');
    const locationStr = filterLocation[0]
      ? `${slugify(common('location'), { lower: true })}=${filterLocation
          .map((location) => slugify(locationTranslate(location), { lower: true }))
          .join(',')}`
      : '';
    const serviceLineStr = filterServiceLine[0]
      ? `&${slugify(category('service_line'), { lower: true })}=${filterServiceLine.map((serviceLine) =>
          slugify(serviceLineTranslate(serviceLine), { lower: true }),
        )}`
      : '';
    const sectorStr = filterSector[0]
      ? `&${slugify(category('sector'), { lower: true })}=${filterSector
          .map((sector) => slugify(sectorTranslate(sector), { lower: true }))
          .join(',')}`
      : '';

    const segmentStr = filterSegment[0]
      ? `&${slugify(category('segment'), { lower: true })}=${filterSegment
          .map((segItem) => slugify(segmentTranslate(segItem), { lower: true }))
          .join(',')}`
      : '';

    const urlParamsStr =
      ((locationStr || serviceLineStr || sectorStr || segmentStr) === '' ? '' : '?') +
      (locationStr + serviceLineStr + sectorStr + segmentStr).replace(/^&/, '');
    urlString = urlString + urlParamsStr;
    router.replace(urlString, undefined, { shallow: true, scroll: false });
  };

  const getServiceLineFilterFromUrl = (paramsObj) => {
    const serviceLineValue = paramsObj[category('service_line').toLowerCase().split(' ').join('-')]
      ?.split(',')
      .map((item) => slugify(item, { lower: true }));
    if (Array.isArray(serviceLineValue) && serviceLineValue.length) {
      const termsArr: any = [];
      ServiceLine.forEach((term) => {
        const translatedTerm = slugify(serviceLineTranslate(term.codename), { lower: true });
        if (serviceLineValue.includes(translatedTerm)) {
          termsArr.push(translatedTerm);
        }
      });
      return termsArr;
    }
    return [];
  };
  const getSectorFilterFromUrl = (paramsObj) => {
    const sectorValue = paramsObj[slugify(category('sector'), { lower: true })]
      ?.split(',')
      .map((item) => slugify(item, { lower: true }));
    if (Array.isArray(sectorValue) && sectorValue.length) {
      const sectorFlattened = flatMapDeep(Sector, getChildren);
      const termsArr: any = [];
      sectorFlattened.forEach((term) => {
        const translatedTerm = slugify(sectorTranslate(term.codename), { lower: true });
        if (sectorValue.includes(translatedTerm)) {
          termsArr.push(translatedTerm);
        }
      });
      return termsArr;
    }
    return [];
  };

  const getSegmentFilterFromUrl = (paramsObj) => {
    const segmentValue = decodeURIComponent(paramsObj[slugify(category('segment'), { lower: true })])
      ?.split(',')
      .map((item) => slugify(item, { lower: true }));
    if (Array.isArray(segmentValue) && segmentValue.length) {
      const termsArr: any = [];
      Segment.forEach((term) => {
        const translatedTerm = slugify(segmentTranslate(term.codename), { lower: true });
        if (segmentValue.includes(translatedTerm)) {
          termsArr.push({ name: translatedTerm, codename: term.codename });
        }
      });
      return termsArr;
    }
    return [];
  };

  const getLocationFilterFromUrl = (paramsObj) => {
    const locationUrl = paramsObj[slugify(common('location'), { lower: true })]
      ?.split(',')
      .map((item) => slugify(item, { lower: true }));
    if (Array.isArray(locationUrl) && locationUrl.length > 0) {
      const locationNames = Location.map((location) => location.codename);
      const matchingLocationNames = locationNames.filter((locationName) =>
        locationUrl.includes(slugify(locationTranslate(locationName), { lower: true })),
      );
      return matchingLocationNames.map(
        (matchingLocationName) => Location.find((location) => location.codename === matchingLocationName)!.codename,
      );
    }
    return [];
  };
  // -- URL based functions End --
  // ------------------------ FUNCTIONS END ------------------------

  // ------------------------ VARIABLES ------------------------
  const [filterLocation, setFilterLocation] = useState<string[]>([]);
  const [filterSector, setFilterSector] = useState<string[]>(urlSector ? [urlSector] : []);
  const [filterSegment, setFilterSegment] = useState<string[]>([]);
  const [filterServiceLine, setFilterServiceLine] = useState<string[]>([]);
  const [amountOfFilters, setAmountOfFilters] = useState(0);
  const [pagesToShow, setPagesToShow] = useState<ContentPage[][]>(dividePagesToShow());
  const [locationState, setLocationState] = useState<OptionsProps[]>([]);
  const [tagsState, setTagsState] = useState<OptionsProps[]>([]);
  const [serviceState, setServiceState] = useState<OptionsProps[]>([]);
  const [segmentState, setSegmentState] = useState<OptionsProps[]>([]);
  const router = useRouter();

  // ------------------------ VARIABLES END ------------------------

  // ------------------------ USE EFFECTS ------------------------
  // useEffect(() => {
  //   const newTags = updateFilterSector();
  //   const newLocations = updateFilterLocation();
  //   const newServices = updateFilterServiceLine();
  //   setTagsState(newTags);
  //   setLocationState(newLocations);
  //   setServiceState(newServices);

  //   setPagesToShow(dividePagesToShow());

  //   const paramsObj = getSearchParams(router.asPath, category);

  //   // If there's a tag in the URL set it to filterSector
  //   const sectorFromUrl = getSectorFilterFromUrl(paramsObj[category('sector')]) > 0 && ;

  //   !urlSector && sectorFromUrl && fetchInitialUrlFilter('sector', sectorFromUrl, setFilterSector, router.locale);
  //   urlSector && fetchInitialUrlFilter('sector', urlSector, setFilterSector, router.locale);
  //   console.log('seactor search param: ', getSearchParams(router.asPath));
  //   // -- Getting Filters from URL --
  //   const serviceLineFromUrl = router.asPath.split('service-line=').slice(-1);
  //   serviceLineFromUrl &&
  //     fetchInitialUrlFilter('service_line', serviceLineFromUrl, setFilterServiceLine, router.locale);
  //   setFilterLocation(getLocationFilterFromUrl());
  // }, []);
  useEffect(() => {
    if (router.locale && router.isReady && translations.isReady) {
      const paramsObj = getSearchParams(router.asPath);
      const newLocations = updateFilterLocation();
      const newTags = updateFilterSector();
      const newSegments = updateFilterSegment();
      const newServices = updateFilterServiceLine();
      setTagsState(newTags);
      setServiceState(newServices);
      setLocationState(newLocations);
      setSegmentState(newSegments);
      setPagesToShow(dividePagesToShow());
      // If there's a tag in the URL set it to filterSector
      urlSector && fetchInitialUrlFilter('sector', urlSector, setFilterSector, router.locale);
      const sectorFromUrl = getSectorFilterFromUrl(paramsObj);

      !urlSector && sectorFromUrl && fetchInitialUrlFilter('sector', sectorFromUrl, setFilterSector, router.locale);
      // -- Getting Filters from URL --
      const serviceLineFromUrl = getServiceLineFilterFromUrl(paramsObj);
      serviceLineFromUrl &&
        fetchInitialUrlFilter('service_line', serviceLineFromUrl, setFilterServiceLine, router.locale);
      const segmentFromUrl = getSegmentFilterFromUrl(paramsObj);

      if (Array.isArray(segmentFromUrl)) {
        const sectorArray = segmentFromUrl
          .map((item) => item.codename)
          .map((segmentCdname) => {
            const mappingCodename = SEGMENT_SECTOR_MAPPING[segmentCdname];
            if (mappingCodename) return slugify(sectorTranslate(mappingCodename), { lower: true });
            return 'no_mapping';
          })
          .filter((item) => item !== 'no_mapping');
        Array.isArray(sectorArray) && fetchInitialUrlFilter('sector', sectorArray, setFilterSector, router.locale);
        fetchInitialUrlFilter(
          'segment',
          segmentFromUrl.map((item) => item.name),
          setFilterSegment,
          router.locale,
        );
      }

      setFilterLocation(getLocationFilterFromUrl(paramsObj));
    }
  }, [router.locale, router.isReady, translations.isReady]);
  // -- Runs when filters change
  useEffect(() => {
    setAmountOfFilters(filterLocation.length + filterSector.length + filterSegment.length + filterServiceLine.length);

    //if (filterLocation?.length && filterSector?.length && filterServiceLine?.length && translations?.isReady)
    if (translations.isReady) updateFiltersInUrl();

    const newTags = updateFilterSector();
    const newLocations = updateFilterLocation();
    const newServices = updateFilterServiceLine();
    const newSegments = updateFilterSegment();

    setTagsState(newTags);
    setLocationState(newLocations);
    setServiceState(newServices);
    setSegmentState(newSegments);

    setPagesToShow(dividePagesToShow());
  }, [filterLocation, filterSector, filterServiceLine, filterSegment]);
  // ------------------------ USE EFFECTS END ------------------------
  //  console.log('filterSector ',filterSector)
  //  console.log('pagesToShow ',pagesToShow)
  return (
    <>
      <HeadComponent page={OverviewPage[0]} />
      {!!FeaturedSpot && <FeaturedContent model={FeaturedSpot} />}
      <OverviewTemplate
        PagesToShow={pagesToShow}
        overviewTitle={multiple('project_title')}
        amountOfFilters={amountOfFilters}
        filterOne={{
          value: filterLocation,
          set: setFilterLocation,
          state: locationState,
          label: 'location',
        }}
        filterTwo={{
          value: filterSector,
          set: setFilterSector,
          state: tagsState,
          label: 'sector',
        }}
        filterThree={{
          value: filterServiceLine,
          set: setFilterServiceLine,
          state: serviceState,
          label: 'service_line',
        }}
        filterFour={{
          value: filterSegment,
          set: setFilterSegment,
          state: segmentState,
          label: 'segment',
          hidden: true,
        }}
      />
    </>
  );
}

export const getStaticProjectsProps = async ({ locale, urlParam }: { locale: string; urlParam?: string }) => {
  const languageCodename = locale ?? process.env.NEXT_PUBLIC_DEFAULT_LOCALE!;
  const ContentPages = await ProdClient.items<ContentPage>()
    .type('content_page')
    .containsFilter('elements.category', ['project'])
    .languageParameter(languageCodename)
    .equalsFilter('system.language', languageCodename)
    .orderByDescending('elements.summary__date')
    .elementsParameter([
      'featured',
      'category',
      'url',
      'summary__title',
      'summary__description',
      'summary__image_frontify',
      'part_of_campaign_page',
      'summary__date',
      'tags__content_type',
      'tags__service_line',
      'tags__sector',
      'tags__location',
      'tags__segment',
    ])
    .collection(process.env.NEXT_PUBLIC_KONTENT_AI_COLLECTION!)
    .toPromise()
    .then((response) => {
      return response.data.items
        .sort((a, b) =>
          Date.parse(a.elements.summaryDate.value ?? a.system.lastModified) <
          Date.parse(b.elements.summaryDate.value ?? b.system.lastModified)
            ? 1
            : -1,
        )
        .sort((a, b) =>
          (a.elements.featured.value[0]?.codename ? a.elements.featured.value[0].codename : 'no') >
          (b.elements.featured.value[0]?.codename ? b.elements.featured.value[0]?.codename : 'no')
            ? -1
            : 1,
        ) as ContentPage[];
    });

  const OverviewPage = await ProdClient.items<OverviewPage>()
    .type('overview_page')
    .languageParameter(languageCodename)
    .containsFilter('elements.category', ['project'])
    .collection(process.env.NEXT_PUBLIC_KONTENT_AI_COLLECTION!)
    .toPromise()
    .then((response) => {
      return response.data.items;
    });

  const FeaturedSpot =
    !!OverviewPage.length &&
    !!OverviewPage[0].elements.hero.value[0] &&
    (await ProdClient.item<FeaturedSpots>(OverviewPage[0].elements.hero.value[0])
      .languageParameter(languageCodename)
      .depthParameter(2)
      .toPromise()
      .then((response) => {
        return response.data.item as FeaturedSpots;
      }));

  const Location =
    cacheData.get('taxonomy_location') == null
      ? await ProdClient.taxonomy('location')
          .toPromise()
          .then((response) => {
            cacheData.put('taxonomy_location', response.data.taxonomy.terms, 86400000, function () {
              console.log('clearing the cache');
              cacheData.del('taxonomy_location'); //to clear the existing cache after 24hr
            });
            return response.data.taxonomy.terms;
          })
      : cacheData.get('taxonomy_location');

  const Sector =
    cacheData.get('taxonomy_sector') == null
      ? await ProdClient.taxonomy('sector')
          .toPromise()
          .then((response) => {
            cacheData.put('taxonomy_sector', response.data.taxonomy.terms, 86400000, function () {
              console.log('clearing the cache');
              cacheData.del('taxonomy_sector'); //to clear the existing cache after 24hr
            });
            return response.data.taxonomy.terms;
          })
      : cacheData.get('taxonomy_sector');

  const ServiceLine =
    cacheData.get('taxonomy_service_line') == null
      ? await ProdClient.taxonomy('service_line')
          .toPromise()
          .then((response) => {
            cacheData.put('taxonomy_service_line', response.data.taxonomy.terms, 86400000, function () {
              console.log('clearing the cache');
              cacheData.del('taxonomy_service_line'); //to clear the existing cache after 24hr
            });
            return response.data.taxonomy.terms;
          })
      : cacheData.get('taxonomy_service_line');

  const Segment =
    cacheData.get('taxonomy_segment') == null
      ? await ProdClient.taxonomy('segment')
          .toPromise()
          .then((response) => {
            cacheData.put('taxonomy_segment', response.data.taxonomy.terms, 86400000, function () {
              console.log('clearing the cache');
              cacheData.del('taxonomy_segment'); //to clear the existing cache after 24hr
            });
            return response.data.taxonomy.terms;
          })
      : cacheData.get('taxonomy_segment');

  const variants = await fetchOverviewVariants('project');

  return {
    ContentPages: ContentPages,
    FeaturedSpot: FeaturedSpot,
    Sector: Sector,
    Segment,
    Location: Location,
    ServiceLine: ServiceLine,
    urlSector: urlParam ?? null,
    OverviewPage: OverviewPage,
    variants,
  };
};
